import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import VerticalStepper from "../../Common/VerticalStepper";

import BasicDetailsForm from "../CreateOffer/Page/BasicDetailsForm";
import RewardForm from "./Page/RewardForm";
import FormFooter from "../CreateOffer/Page/FormFooter";
import Preview from "../CreateOffer/Page/Preview";

import { createPartnerOffer } from "../../../slices/RedemptionOffer/CreateOffer/thunk";
import { updatePartnerOffer } from "../../../slices/RedemptionOffer/UpdateOffer/thunk";

const OffersFormCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isEdit = location.state?.isEdit === true;
  const editOffer = location.state?.offer || null;

  const [activeStep, setActiveStep] = useState(1);
  const [basicDetails, setBasicDetails] = useState({});
  const [rewardDetails, setRewardDetails] = useState({});
  const [publishing, setPublishing] = useState(false);

  const operatorPartnerId = localStorage.getItem("operatorId");

  const [operatorId, partnerId] = operatorPartnerId
    ? operatorPartnerId.split("/")
    : [null, null];

  const partnerName = localStorage.getItem("userName");

  const steps = [
    {
      title: "Basic Details",
      description: "Title, description & image",
    },
    {
      title: "Reward Details",
      description: "Points, discount & validity",
    },
    {
      title: "Preview",
      description: "Review before publishing",
    },
  ];

  const getInitialBasicDetails = (offer) => {
    if (!offer) {
      return {};
    }

    const bannerImage = offer.bannerImage;

    return {
      offertitle: offer.offerTitle || "",
      description: offer.description || "",
      category: offer.category || "",

      bannerimage: bannerImage
        ? {
            id:
              typeof bannerImage === "object"
                ? bannerImage.id ||
                  bannerImage.imageId ||
                  bannerImage.fileId ||
                  null
                : bannerImage,

            name:
              typeof bannerImage === "object"
                ? bannerImage.name ||
                  bannerImage.fileName ||
                  "Existing banner image"
                : "Existing banner image",

            url:
              typeof bannerImage === "object"
                ? bannerImage.url ||
                  bannerImage.fileUrl ||
                  bannerImage.imageUrl ||
                  bannerImage.downloadUrl ||
                  bannerImage.publicUrl ||
                  bannerImage.location ||
                  ""
                : bannerImage,
          }
        : null,
    };
  };

  const getInitialRewardDetails = (offer) => {
    if (!offer) {
      return {};
    }

    return {
      pointsrequired: offer.pointsRequired ?? "",
      discounttype:
        offer.discountType?.toLowerCase() === "percentage"
          ? "percentage"
          : "flat",
      discountvalue: offer.discountValue ?? "",
      minOrderValue: offer.minOrderValue ?? "",
      startdate: offer.validityStart
        ? offer.validityStart.substring(0, 10)
        : "",
      enddate: offer.validityEnd ? offer.validityEnd.substring(0, 10) : "",
    };
  };

  useEffect(() => {
    if (isEdit && editOffer) {
      const basicData = getInitialBasicDetails(editOffer);
      const rewardData = getInitialRewardDetails(editOffer);

      setBasicDetails(basicData);
      setRewardDetails(rewardData);
    }
  }, [isEdit, editOffer]);

  const validateBasicDetails = () => {
    if (!basicDetails?.offertitle?.trim()) {
      toast.warning("Please fill the required fields");
      return false;
    }

    if (!basicDetails?.category?.trim()) {
      toast.warning("Please fill the required fields");
      return false;
    }

    if (!basicDetails?.bannerimage) {
      toast.warning("Please fill the required fields");
      return false;
    }

    return true;
  };

  const validateRewardDetails = () => {
    if (
      !rewardDetails?.pointsrequired ||
      Number(rewardDetails.pointsrequired) <= 0
    ) {
      toast.warning("Please fill the required fields");
      return false;
    }

    if (
      !rewardDetails?.discountvalue ||
      Number(rewardDetails.discountvalue) <= 0
    ) {
      toast.warning("Please fill the required fields");
      return false;
    }

    if (
      rewardDetails?.minOrderValue === "" ||
      rewardDetails?.minOrderValue === undefined ||
      rewardDetails?.minOrderValue === null
    ) {
      toast.warning("Please fill the required fields");
      return false;
    }

    if (!rewardDetails?.startdate) {
      toast.warning("Please fill the required fields");
      return false;
    }

    if (!rewardDetails?.enddate) {
      toast.warning("Please fill the required fields");
      return false;
    }

    if (
      rewardDetails?.startdate &&
      rewardDetails?.enddate &&
      new Date(rewardDetails.enddate) < new Date(rewardDetails.startdate)
    ) {
      toast.warning("End date must be after start date");
      return false;
    }

    return true;
  };

  const handleContinue = () => {
    if (activeStep === 1) {
      if (!validateBasicDetails()) {
        return;
      }
    }

    if (activeStep === 2) {
      if (!validateRewardDetails()) {
        return;
      }
    }

    if (activeStep < steps.length) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 1) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleStepClick = (step) => {
    if (step === 2 && activeStep === 1) {
      if (!validateBasicDetails()) {
        return;
      }
    }

    if (step === 3) {
      if (!validateBasicDetails()) {
        setActiveStep(1);
        return;
      }

      if (!validateRewardDetails()) {
        setActiveStep(2);
        return;
      }
    }

    if (step <= activeStep) {
      setActiveStep(step);
      return;
    }

    if (step === activeStep + 1) {
      setActiveStep(step);
    }
  };

  const buildOfferPayload = (status) => {
    const bannerImage = basicDetails?.bannerimage;

    const bannerImageId =
      typeof bannerImage === "object"
        ? bannerImage?.id || bannerImage?.imageId || bannerImage?.fileId || null
        : null;

    const payload = {
      offerTitle: basicDetails?.offertitle || "",
      description: basicDetails?.description || "",
      category: basicDetails?.category || "",
      customCategory: null,

      bannerImage: bannerImageId
        ? {
            id: bannerImageId,
          }
        : null,

      partnerId: partnerId,
      partnerName: partnerName,
      operatorId: operatorId,

      pointsRequired: Number(rewardDetails?.pointsrequired || 0),

      discountType: rewardDetails?.discounttype || "flat",

      discountValue: Number(rewardDetails?.discountvalue || 0),

      minOrderValue: Number(rewardDetails?.minOrderValue || 0),

      validityStart: rewardDetails?.startdate
        ? new Date(`${rewardDetails.startdate}T00:00:00.000Z`).toISOString()
        : null,

      validityEnd: rewardDetails?.enddate
        ? new Date(`${rewardDetails.enddate}T23:59:59.999Z`).toISOString()
        : null,

      status: status,
    };
    
    return payload;
  };

  // PUBLISH OFFER
  const handlePublish = () => {
    if (!validateBasicDetails()) {
      setActiveStep(1);
      return;
    }

    if (!validateRewardDetails()) {
      setActiveStep(2);
      return;
    }

    const payload = buildOfferPayload("active");

    if (isEdit) {
      if (!editOffer?.id) {
        toast.error("Offer ID not found");
        return;
      }

      dispatch(
        updatePartnerOffer(
          payload,
          editOffer.id,
          setPublishing,
          () => {
            navigate("/offers");
          },
          "Offer updated successfully",
        ),
      );

      return;
    }

    dispatch(
      createPartnerOffer(payload, setPublishing, () => {
        navigate("/offers");
      }),
    );
  };

  // SAVE DRAFT
  const handleSaveDraft = () => {
    if (!validateBasicDetails()) {
      setActiveStep(1);
      return;
    }

    if (!validateRewardDetails()) {
      setActiveStep(2);
      return;
    }

    const payload = buildOfferPayload("draft");

    if (isEdit) {
      if (!editOffer?.id) {
        toast.error("Offer ID not found");
        return;
      }

      dispatch(
        updatePartnerOffer(
          payload,
          editOffer.id,
          setPublishing,
          () => {
            navigate("/offers");
          },
          "Offer saved successfully as a draft",
        ),
      );

      return;
    }
  };

  const previewData = {
    ...basicDetails,
    ...rewardDetails,
  };

  return (
    <div className="container-fluid d-flex flex-column min-vh-100">
      <div className="row flex-grow-1">
        {activeStep === 3 ? (
          <>
            <div className="col-lg-3">
              <VerticalStepper
                steps={steps}
                activeStep={activeStep}
                onStepClick={handleStepClick}
              />
            </div>

            <div className="col-lg-9">
              <Preview
                data={previewData}
                onEditBasic={() => setActiveStep(1)}
                onEditReward={() => setActiveStep(2)}
              />
            </div>
          </>
        ) : (
          <>
            <div className="col-lg-3">
              <VerticalStepper
                steps={steps}
                activeStep={activeStep}
                onStepClick={handleStepClick}
              />
            </div>

            <div className="col-lg-1" />

            <div className="col-lg-6">
              {activeStep === 1 && (
                <BasicDetailsForm
                  initialData={basicDetails}
                  onDataChange={setBasicDetails}
                />
              )}

              {activeStep === 2 && (
                <RewardForm
                  initialData={rewardDetails}
                  onDataChange={setRewardDetails}
                />
              )}
            </div>

            <div className="col-lg-2" />
          </>
        )}
      </div>

      <FormFooter
        activeStep={activeStep}
        totalSteps={steps.length}
        onContinue={handleContinue}
        onBack={handleBack}
        onSaveDraft={handleSaveDraft}
        onPublish={handlePublish}
        publishing={publishing}
      />
    </div>
  );
};

export default OffersFormCreate;
